// Singleton — Pyodide loads once per browser session and is shared across all workspaces
let pyodideInstance = null;
let loadingPromise = null;

// Tracks whether Pyodide is currently bootstrapping so CodeRunner can show a spinner
export let pyodideLoading = false;

const PYODIDE_VERSION = '0.27.0';
const CDN = `https://cdn.jsdelivr.net/pyodide/v${PYODIDE_VERSION}/full/`;

function injectScript() {
  return new Promise((resolve, reject) => {
    if (window.loadPyodide) return resolve();
    const script = document.createElement('script');
    script.src = `${CDN}pyodide.js`;
    script.onload = resolve;
    script.onerror = () => reject(new Error('Failed to load Pyodide script'));
    document.head.appendChild(script);
  });
}

async function bootPyodide() {
  await injectScript();
  // Boot only — packages are loaded on demand from the user's actual imports
  return await window.loadPyodide({ indexURL: CDN });
}

export async function getPyodide() {
  if (pyodideInstance) return pyodideInstance;
  if (!loadingPromise) {
    pyodideLoading = true;
    loadingPromise = bootPyodide()
      .then((py) => {
        pyodideInstance = py;
        pyodideLoading = false;
        return py;
      })
      .catch((e) => {
        loadingPromise = null;
        pyodideLoading = false;
        throw e;
      });
  }
  return loadingPromise;
}

const CHART_HELPER = `
import sys as _sys, io as _io, base64 as _b64
_orig_stdout = _sys.stdout
_orig_stderr = _sys.stderr

def _show_chart():
    import matplotlib.pyplot as _plt
    _buf = _io.BytesIO()
    _plt.savefig(_buf, format='png', bbox_inches='tight', dpi=100)
    _plt.close('all')
    _buf.seek(0)
    _orig_stdout.write('__CHART__:' + _b64.b64encode(_buf.read()).decode() + '\\n')
`;

export async function runPython(code) {
  const py = await getPyodide();

  // Load only the packages actually imported in this code — cached after first load
  try {
    await py.loadPackagesFromImports(code);
  } catch {
    // Non-fatal: some import strings may not map to a Pyodide package
  }

  const outBuf = [];
  const errBuf = [];
  py.setStdout({ batched: (s) => outBuf.push(s) });
  py.setStderr({ batched: (s) => errBuf.push(s) });

  try {
    await py.runPythonAsync(CHART_HELPER + '\n' + code);
  } catch (e) {
    // Strip Pyodide internal frames — keep only the user-relevant part
    const msg = e.message || String(e);
    const trimmed = msg.includes('File "<exec>"')
      ? msg.slice(msg.indexOf('File "<exec>"'))
      : msg;
    errBuf.push(trimmed);
  } finally {
    py.setStdout({});
    py.setStderr({});
  }

  const rawOut = outBuf.join('');
  const errorText = errBuf.join('');

  // Separate chart data from text output
  const lines = rawOut.split('\n');
  const chartLine = lines.find((l) => l.startsWith('__CHART__:'));
  const textOutput = lines.filter((l) => !l.startsWith('__CHART__:')).join('\n').trimEnd();

  return {
    output: textOutput,
    error: errorText,
    chart: chartLine ? chartLine.slice('__CHART__:'.length) : null,
  };
}

export const CHART_SNIPPET = `# To render a chart, use matplotlib then call _show_chart()
import matplotlib
matplotlib.use('Agg')
import matplotlib.pyplot as plt

# ... your plotting code ...
plt.title('My Chart')

_show_chart()  # renders the chart inline`;
